import { CloseOutlined } from '@ant-design/icons';
import { ArrowBackIos, ArrowForwardIos } from '@mui/icons-material';
import { Row } from "antd";
import React, { useEffect, useRef, useState } from 'react';
import { usePDFView } from 'store/main_book_popup/selectors';
import styled from "styled-components";
import COLOR from 'utils/constants/color';
import Loading from './Loading';
import usePopUpAtOnce from './hooks/usePopUpAtOnce';
import { useLocation } from 'react-router-dom';
import { isMobileDevice } from 'utils/methods/math';
import { useSideDrawer } from 'utils/context/SideDrawerProvider';
import { useFullScreen } from 'utils/context/FullScreenProvider';
import { useOrientation } from './hooks';

const Wrapper = styled.div`
    position: absolute;
    top: ${p => p.pathname?.includes('ebook') ? '40px' : '0px'};
    width: ${p => p.width}px !important;
    height: ${p => p.height}px !important;
    background-color: rgba(0, 0, 0,0.3);
    z-index: 2;
    box-shadow: 7px -3px 26px 20px rgb(66 66 66), -4px -3px 26px 15px rgb(95 109 110);
`;

const ButtonContainer = styled.button`
    background-color: transparent;
    border: none;
    color: ${COLOR.black600};
    cursor: pointer;
    outline: none;
    transition: all 300ms ease;
    width: 40px;
    z-index: 1;
    display:flex;
    justify-content: center;
    align-items: center;
`;

const PDFViewer = styled.iframe`
    width: 100%;
    height: 100%;
    border: none;
    background-color: white;
    pointer-events: auto;
    overflow-y: auto;
    overflow-x: hidden;
    -webkit-overflow-scrolling: touch;
    touch-action: pan-y;
    flex: 1;
    min-height: 0;
`;

export default function PDFPopup({ getRef, onClose }) {
    const { pdfView, revokePDFView } = usePDFView();
    const [pdfUrl, setPdfUrl] = useState(null);
    const [style, setStyle] = useState({ height: 0, width: 0 });
    const backBtnRef = useRef();
    const forwardBtnRef = useRef();
    const { updatePopUpAtOnce } = usePopUpAtOnce();
    const location = useLocation();
    const isLandscape = useOrientation();
    const isMobile = isMobileDevice();

    const { currentFullScreen } = useFullScreen();

    useEffect(() => {
        if (pdfView.open) {
            setPdfUrl(pdfView.pdf.resource);
        } else {
            setPdfUrl(null);
        }
    }, [pdfView]);

    useEffect(() => {
        const el = getRef()?.current || getRef();
        if (!el) return;
    
        const observer = new ResizeObserver(entries => {
            const rect = entries[0].contentRect;
            setStyle({
                width: rect.width,
                height: rect.height
            });
        });
    
        observer.observe(el);
    
        return () => observer.disconnect();
    }, []);
    
    useEffect(() => {
        document.addEventListener('keydown', checkKey);
        return () => document.removeEventListener('keydown', checkKey);
    }, []);

    const handleClose = () => {
        revokePDFView();
        updatePopUpAtOnce(false);
        onClose();
    };

    function checkKey(e) {
        const isMainBook = document.getElementsByClassName("main-book-popup printScreenPage").length > 0;
        if (!isMainBook) return;

        if (e.keyCode === 37 && backBtnRef.current && !backBtnRef.current.disabled) {
            backBtnRef.current.click();
        }

        if (e.keyCode === 39 && forwardBtnRef.current && !forwardBtnRef.current.disabled) {
            forwardBtnRef.current.click();
        }
    }

    if (!pdfView.open) return null;

    const class_name = pdfView.type === 'page_translation'
        ? 'printScreenPage'
        : 'main-book-popup printScreenPage videoPrintScreenCstm';

    return (
        <Wrapper pathname={location.pathname} width={style.width} height={style.height + 36} className={class_name}>
            <div className='pdfMdlClose'>
              <CloseOutlined onClick={handleClose} />
            </div>
            {isMobile ? (
                <PDFViewer
                    className='check-frame'
                    key={style.width + style.height}
                    src={`https://docs.google.com/gview?embedded=true&url=${encodeURIComponent(pdfUrl)}`}
                    title="PDF Preview"
                />
            ) : (
            <PDFViewer
                className='check-frame'
                key={style.width + style.height}
                src={`${pdfUrl}#toolbar=0&navpanes=0&scrollbar=1&view=FitH`}
                onLoad={() =>
                    setTimeout(() => {
                        const el = getRef()?.current || getRef();
                        if (!el) return;
                        const rect = el.getBoundingClientRect();
                        setStyle({ width: rect.width, height: rect.height });
                    }, 180)
                }
                title="PDF Preview"
                onContextMenu={(e) => e.preventDefault()}
            />
            )}
        </Wrapper>
    );
}
